import lodashGet from "lodash/get";
import { encodeAllURLParameters } from "@/common/api.utilities";
import { ENDPOINT_WOD_CHARTS } from "@/common/constants/api";
import { httpGet } from "@/services/api.service";
import { getSortedPrimaryTopics } from "@/services/events/topics.service";
import { isFeatureUnlocked } from "@/common/featureLock";
export { getChartData, formatChartData, formatTopicChartData };

/**
 * Gets the chart data for the query defined by `params`
 * @param params {Object} Defines the query parameters for the request to /wod/charts
 * @returns {chartData}
 */
const getChartData = async (params) => {
  const endpoint = encodeAllURLParameters({
    baseEndpoint: ENDPOINT_WOD_CHARTS,
    params: params,
  });
  const result = JSON.parse(
    await httpGet({
      url: endpoint,
    })
  ).data;

  return result;
};

/**
 * Gets the chart data for the query defined by `params` and formats the response for the chart types specified by `options`
 * @param params {Object} Defines the query parameters for the request to /wod/charts
 * @param options {
 * emotionChartData: Boolean,
 * eventsSentimentData: Boolean,
 * sectorChartData: Boolean,
 * attackChartData: Boolean,
 * perpetratorChartData: Boolean,
 * vesselChartData: Boolean,
 * timeChartData: Boolean,
 * } Specifies which data to format and return.
 * @returns {emotionChartData: {xAxis: [], seriesData:[]}}
 */
const formatChartData = (data, options) => {
  let charts = {};
  if (options.emotionChartData) {
    charts.emotionChartData = _getSeriesData(data, {
      seriesType: "line",
      aggregationName: "emotionsHistogramByDates",
      bucketName: "emotions.counts",
    });
  }
  if (options.eventsSentimentData) {
    charts.eventsSentimentData = lodashGet(
      data?.aggregations,
      "sentimentHistogram"
    )?.buckets;
  }
  if (options.sectorChartData) {
    charts.sectorChartData = _getBucketData(data, "sectorTypes.sectors");
  }
  if (options.attackChartData) {
    charts.attackChartData = _getBucketData(data, "attackTypes.attacks");
  }
  if (options.perpetratorChartData) {
    charts.perpetratorChartData = _getBucketData(
      data,
      "perpetratorTypes.perpetrators"
    );
  }
  if (options.vesselChartData) {
    charts.vesselChartData = _getBucketData(data, "vesselTypes");
  }
  if (options.timeChartData) {
    charts.timeChartData = _getBucketData(data, "hourOfDay");
  }
  return charts;
};

/**
 *
 * @param options
 * @property {string} [options.aggregationName=dateRange] - The name of the aggregation to get series data for
 * @property {string} [options.bucketName] - The name of the aggregation bucket to get series data for. If not supplied, just the aggregation doc_count will be used.
 * @property {string} [options.seriesType=line] - The series type (line, scatter, etc)
 * @returns {{}|{series: *[], xAxis: *[]}}
 */
const _getSeriesData = (
  data,
  options = {
    aggregationName: "dateRange",
    bucketName: "",
    seriesType: "line",
  }
) => {
  const allBuckets = lodashGet(
    data?.aggregations,
    options.aggregationName || "dateRange"
  )?.buckets;

  if (!allBuckets || !allBuckets.length) {
    return {};
  }
  const xAxis = [];
  const graphData = [];
  const allSeriesData = [];

  if (!options.bucketName) {
    allSeriesData.push({
      name: "Number of Events",
      type: options.seriesType || "line",
      data: new Array(allBuckets.length).fill(0, 0),
    });
  }

  allBuckets.forEach((singleDayBuckets, index) => {
    xAxis.push(singleDayBuckets.key_as_string);

    if (!options.bucketName) {
      //Update the series with the doc count at this index
      allSeriesData[0].data[index] = singleDayBuckets.doc_count;
      return;
    }

    for (const [k, bucket] of Object.entries(
      lodashGet(singleDayBuckets, options.bucketName)?.buckets || {}
    )) {
      let category = null;
      if (Array.isArray(bucket)) {
        category = bucket.key;
      } else {
        category = k;
      }

      if (graphData[category]) {
        graphData[category].data[index] = bucket.doc_count;
      } else {
        //Create a starter array with 0s
        const values = new Array(allBuckets.length);
        values.fill(0, 0);
        //Update the series with the doc count at this index
        values[index] = bucket.doc_count;

        //Create an object for this series
        const seriesData = {
          name: category.replace("_", " "),
          type: options.seriesType || "line",
          data: values,
        };

        //Save the series object
        graphData[category] = seriesData;
        allSeriesData.push(seriesData);
      }
    }
  });

  return {
    xAxis: xAxis,
    series: allSeriesData,
  };
};

const _getBucketData = (data, aggregateName) => {
  let buckets = lodashGet(data.aggregations, aggregateName)?.buckets;
  if (!buckets) {
    return {};
  }
  let ret = {};
  ret.xAxis = [];
  ret.seriesData = [];
  buckets.forEach((bucket) => {
    let x_axis_key = bucket.key.toString();
    ret.xAxis.push(x_axis_key);
    ret.seriesData.push(bucket.doc_count);
  });

  return ret;
};

const formatTopicChartData = (topicChartData, chartType) => {
  if (!topicChartData || !topicChartData.series || !chartType) {
    return {};
  }

  //Sort and filter out hidden topics
  let topics = getSortedPrimaryTopics(chartType);
  let newSeries = [];

  //Add back the total series, which is not a topic from topic config
  let totalSeries = topicChartData.series.find((s) => s.id === "total");
  newSeries.push(totalSeries);

  //Iterate over each topic to check UI topic config properties
  topics.forEach((topic) => {
    //Filter out locked topics
    if (isFeatureUnlocked(`event-${topic.type}`)) {
      let matchingSeries = topicChartData.series.find(
        (s) => s.id === topic.apiName
      );
      //Update the display name to match the topic filters in search
      matchingSeries.name = topic.plural;

      newSeries.push(matchingSeries);
    }
  });

  topicChartData.series = newSeries;

  return topicChartData;
};
